import { DoughnutChartColors } from "@/constants/charts.ts";
import { WorkspaceType } from "@/constants/workplaces.ts";

// Workspace types that are backed up themselves and so get Cloud Backup in single view:
// a child, and a channel protecting its own workspace. A regular workspace has no MSP to
// hold its Keepit account.
export const CLOUD_BACKUP_WORKSPACE_TYPES: WorkspaceType[] = [
  WorkspaceType.CHILD,
  WorkspaceType.CHANNEL,
];

export enum BackupAndRestoreAction {
  CREATE_MSP_ACCOUNT = "createMspBackupAccount",
  CREATE_CUSTOMER_ACCOUNT = "createBackupCustomerAccount",
  EDIT_CUSTOMER_ACCOUNT = "editBackupCustomerAccount",
}

// Per-row actions offered by the child-workspace table's overflow menu.
export enum BackupChildAction {
  VIEW_IN_KEEPIT = "viewInKeepit",
  EDIT = "edit",
}

// Per-job outcome buckets reported by the backup/restore dashboard endpoint
// (GET /msp/dashboard/backupAndRestore). Values match the contract response keys.
export enum BackupAndRestoreJobStatus {
  SCHEDULED = "scheduled",
  IN_PROGRESS = "in-progress",
  UNSUCCESSFUL = "unsuccessful",
  INCOMPLETE = "incomplete",
  CANCELLED = "cancelled",
  SUCCESSFUL = "successful",
}

// Display order for the backup/restore dashboard legend and doughnut segments.
export const BACKUP_AND_RESTORE_STATUS_ORDER: BackupAndRestoreJobStatus[] = [
  BackupAndRestoreJobStatus.SUCCESSFUL,
  BackupAndRestoreJobStatus.IN_PROGRESS,
  BackupAndRestoreJobStatus.SCHEDULED,
  BackupAndRestoreJobStatus.INCOMPLETE,
  BackupAndRestoreJobStatus.CANCELLED,
  BackupAndRestoreJobStatus.UNSUCCESSFUL,
];

// Maps each job status to its doughnut/legend color.
export const BACKUP_AND_RESTORE_STATUS_COLORS: Record<BackupAndRestoreJobStatus, string> = {
  [BackupAndRestoreJobStatus.SUCCESSFUL]: DoughnutChartColors.SUCCESS,
  [BackupAndRestoreJobStatus.IN_PROGRESS]: DoughnutChartColors.BLUE,
  [BackupAndRestoreJobStatus.SCHEDULED]: DoughnutChartColors.PINK,
  [BackupAndRestoreJobStatus.INCOMPLETE]: DoughnutChartColors.ORANGE,
  [BackupAndRestoreJobStatus.CANCELLED]: DoughnutChartColors.GRAY,
  [BackupAndRestoreJobStatus.UNSUCCESSFUL]: DoughnutChartColors.ERROR,
};

/** Job counts per outcome for a single backup/restore direction. */
export type BackupAndRestoreJobStats = Record<BackupAndRestoreJobStatus, number>;

/**
 * Job counts for a direction plus the timestamp of its most recent run.
 * `lastBackupTime`/`lastRestoreTime` are Unix ms and may be absent when the
 * account has never run that job type.
 */
export type BackupAndRestoreDirectionStats = BackupAndRestoreJobStats & {
  lastBackupTime?: number;
  lastRestoreTime?: number;
};

/** Response shape of GET /msp/dashboard/backupAndRestore. */
export interface BackupAndRestoreWidgetData {
  backups: BackupAndRestoreDirectionStats;
  restores: BackupAndRestoreDirectionStats;
}

export enum BackupAndRestoreStatus {
  NOT_CONNECTED = "NOT_CONNECTED",
  CONNECTED = "CONNECTED",
  PARENT_NOT_ACTIVATED = "PARENT_NOT_ACTIVATED",
  PROVISIONING = "PROVISIONING",
  FAILED = "FAILED",
  PENDING_DELETION = "PENDING_DELETION",
}

// How long backup data is retained for a customer account.
export enum RetentionType {
  YEARLY = "YEARLY",
  UNLIMITED = "UNLIMITED",
}

// Keepit backup workloads (connector types), excluding clouds and drives.
// Values are Keepit's API resource names. See:
// https://developers.keepit.com/resource-names#cloud-devices
export enum KeepitWorkload {
  MICROSOFT365 = "o365-admin",
  DYNAMICS365 = "dynamics365",
  ENTRA_ID = "azure-ad",
  AZURE_DEVOPS = "azure-do",
  POWER_BI = "powerbi",
  GOOGLE_WORKSPACE = "gsuite",
  SALESFORCE = "salesforce",
  ZENDESK = "zendesk",
  BAMBOO_HR = "bamboohr-connector",
  CONFLUENCE = "confluence-connector",
  JIRA = "jira-connector",
  MIRO = "miro-connector",
  MONDAY = "monday-connector",
  DOCUSIGN = "docusign-connector",
  OKTA = "okta-connector",
  GITHUB = "github-connector",
  AUTODESK = "autodesk-forma-connector",
}

// Maps each backup workload to its brand logo in /public/images/app-service-logos.
// Standalone (full-color) SVGs, so they are referenced directly by path rather
// than through the icon sprite.
export const KEEPIT_WORKLOAD_ICONS: Partial<Record<KeepitWorkload, string>> = {
  [KeepitWorkload.MICROSOFT365]: "icon-service-logo-microsoft-365.svg",
  [KeepitWorkload.ENTRA_ID]: "icon-service-entra-id.svg",
  [KeepitWorkload.AZURE_DEVOPS]: "icon-service-azure-devops.svg",
  [KeepitWorkload.POWER_BI]: "icon-service-power-bi.svg",
  [KeepitWorkload.GOOGLE_WORKSPACE]: "icon-service-logo-google-workspace.svg",
  [KeepitWorkload.SALESFORCE]: "icon-service-sales-force.svg",
  [KeepitWorkload.ZENDESK]: "icon-service-zendesk.svg",
  [KeepitWorkload.BAMBOO_HR]: "icon-service-bamboohr.svg",
  [KeepitWorkload.CONFLUENCE]: "icon-service-confluence.svg",
  [KeepitWorkload.JIRA]: "icon-service-jira.svg",
  [KeepitWorkload.MIRO]: "icon-service-miro.svg",
  [KeepitWorkload.MONDAY]: "icon-service-monday.svg",
  [KeepitWorkload.DOCUSIGN]: "icon-service-docusign.svg",
  [KeepitWorkload.OKTA]: "icon-service-okta.svg",
  [KeepitWorkload.GITHUB]: "icon-service-github.svg",
  [KeepitWorkload.DYNAMICS365]: "icon-service-dynamics-365.svg",
  [KeepitWorkload.AUTODESK]: "icon-service-autodesk.svg",
};

// Connection state of a single Keepit workload for a child workspace.
export enum WorkloadConnectionStatus {
  CONNECTED = "CONNECTED",
  DISCONNECTED = "DISCONNECTED",
}

// Backup health of a single Keepit workload for a child workspace.
export enum WorkloadHealthStatus {
  HEALTHY = "HEALTHY",
  UNHEALTHY = "UNHEALTHY",
  CRITICAL = "CRITICAL",
}

// Worst-to-best ranking used to roll per-workload health up to a single
// row-level status (the highest severity among connected workloads wins).
export const WORKLOAD_HEALTH_SEVERITY: Record<WorkloadHealthStatus, number> = {
  [WorkloadHealthStatus.CRITICAL]: 3,
  [WorkloadHealthStatus.UNHEALTHY]: 2,
  [WorkloadHealthStatus.HEALTHY]: 1,
};

/** A single backed-up cloud app (workload) for a child workspace. */
export interface BackupChildWorkload {
  name: KeepitWorkload;
  status: WorkloadConnectionStatus;
  healthStatus: WorkloadHealthStatus;
  limit: number;
  usage: number;
}

/**
 * Response item shape of GET /backupAndRestore/connectors — the workspace's own
 * cloud apps. Identical to a child workspace's workload, so it is an alias rather
 * than a second declaration of the same fields; the name keeps the workspace call
 * sites readable.
 */
export type BackupConnector = BackupChildWorkload;

// The four Overview tiles. Each one navigates to the tab that explains its number.
export enum BackupSummaryTile {
  TOTAL_BACKUPS = "totalBackups",
  ITEMS_RESTORED = "itemsRestored",
  BACKED_UP_USERS = "backedUpUsers",
  NEEDS_ATTENTION = "needsAttention",
}

/**
 * The `?type=` query param of GET /backupAndRestore/jobs.
 *
 * Named "direction" rather than "type" on purpose: a job row also has a `type`,
 * and that one is the cloud app it ran against (a `KeepitWorkload`), not this.
 */
export enum BackupJobDirection {
  BACKUP = "backup",
  RESTORE = "restore",
}

/** Response item shape of GET /backupAndRestore/jobs. Times are Unix ms. */
export interface BackupJob {
  type: BackupJobDirection;
  connector: KeepitWorkload;
  status: BackupAndRestoreJobStatus;
  description: string;
  startTime: number;
  endTime: number;
}

// Status column of the jobs tables. Kept apart from BACKUP_AND_RESTORE_STATUS_COLORS
// because the table colors text, where the dashboard fills doughnut segments, and the
// two palettes are not interchangeable.
export const BACKUP_JOB_STATUS_TEXT_CLASSES: Record<BackupAndRestoreJobStatus, string> = {
  [BackupAndRestoreJobStatus.SUCCESSFUL]: "text-green-dark",
  [BackupAndRestoreJobStatus.IN_PROGRESS]: "text-indigo-medium",
  [BackupAndRestoreJobStatus.SCHEDULED]: "text-indigo-medium",
  [BackupAndRestoreJobStatus.INCOMPLETE]: "text-yellow-base",
  [BackupAndRestoreJobStatus.CANCELLED]: "text-indigo-medium",
  [BackupAndRestoreJobStatus.UNSUCCESSFUL]: "text-red-dark",
};

/**
 * A Keepit child workspace. Response item shape of
 * GET /msp/backupAndRestore/children (MSP workspace id passed as a header).
 */
export interface BackupChild {
  name: string;
  workspaceId: string;
  // CONNECTED, or PENDING_DELETION while Backup & Restore is being removed from the child.
  status: BackupAndRestoreStatus;
  // When a PENDING_DELETION child is removed for good (Unix ms). Null until its Keepit
  // account is actually deleted, and for every connected child.
  scheduledForDeletionAt: number | null;
  workloads: BackupChildWorkload[];
}

/** Payload of the child-workspace table's `action-applied` event. */
export interface BackupChildActionPayload {
  action: BackupChildAction;
  item: BackupChild;
}

// Row-level connection status for a child workspace, rolled up from its workloads.
export enum BackupChildConnectionStatus {
  CONNECTED = "CONNECTED",
  PARTIAL = "PARTIAL",
  NOT_CONNECTED = "DISCONNECTED",
}

// Row-level health status for a child workspace: the worst health among connected
// workloads, or NONE when nothing is connected.
export enum BackupChildHealthStatus {
  HEALTHY = "HEALTHY",
  UNHEALTHY = "UNHEALTHY",
  CRITICAL = "CRITICAL",
  NONE = "NONE",
}

// Maps a single-workload health value to its row-level equivalent.
export const WORKLOAD_TO_CHILD_HEALTH: Record<WorkloadHealthStatus, BackupChildHealthStatus> = {
  [WorkloadHealthStatus.HEALTHY]: BackupChildHealthStatus.HEALTHY,
  [WorkloadHealthStatus.UNHEALTHY]: BackupChildHealthStatus.UNHEALTHY,
  [WorkloadHealthStatus.CRITICAL]: BackupChildHealthStatus.CRITICAL,
};
